import { LeaseColumns } from '@miq/catalog';
import { FormGroup } from '@angular/forms';

import {
  isValidSpaceTenancyStatus,
  leaseDateValidator,
  notEmptyRentUOM,
  notEmptySpaceUOM,
  spaceSizeLessThanFloorSize,
} from '../details-modal.validators';
import { DetailsOverlayData } from '../../details-overlay/details-overlay.model';
import { areaUomAbbrev, formatNumber, isMasked } from '@miq/utils';
import { TenantSpaceClass } from '../../../../../../models/space/tenant-space.class';
import { formatDate, generateLastUpdatedByMessage } from '../../../../../../shared/utils/functions';
import { getColumnLabel, yearsAndMonthsTerm } from '../details-modal.constants';
import { SuiteType } from '../../item.model';
import { AreaUrl } from '../../details-overlay/details-overlay.constants';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DetailsSpace } from '../details-modal.model';

export const LEASE_CONFIG: DetailsOverlayData<TenantSpaceClass> = {
  id: space => space.tenantId.toString(),
  title: space => space.companyName,
  navigateToEntity: space => {
    const entityId = space.leaseId;
    return entityId ? `records/lease/${entityId}/edit` : null;
  },
  validators: [
    {
      groupName: 'formGroupSpace',
      validator: [notEmptySpaceUOM, spaceSizeLessThanFloorSize],
    },
    { groupName: 'formGroupRentRate', validator: [notEmptyRentUOM] },
  ],
  actions: [
    {
      label: 'overlayDetails.useTermDates',
      inputType: 'checkbox',
      actionFn: (event: MatCheckboxChange, detailsFormGroup: FormGroup, space: DetailsSpace) => {
        const { lease } = space;
        if (event.checked) {
          detailsFormGroup.controls['leaseStartDate'].patchValue(lease.lease_start_date);
          detailsFormGroup.controls['leaseEndDate'].patchValue(lease.lease_end_date);
          detailsFormGroup.controls['spaceOccupancyDate'].patchValue(lease.occupancy_date);
          return;
        }
        detailsFormGroup.controls['leaseStartDate'].patchValue(lease.space_tenancy_start_date);
        detailsFormGroup.controls['leaseEndDate'].patchValue(lease.space_tenancy_end_date);
        detailsFormGroup.controls['spaceOccupancyDate'].patchValue(lease.space_occupancy_date);
      },
    },
  ],
  summaryLabels: [
    {
      label: 'overlayDetails.leaseTermData',
      fields: [
        {
          label: 'shared.propertyNavigationTable.areaLeased',
          displayFn: ({ lease }) => {
            const leasedSpaceSize = lease?.total_leased_space;
            const leasedSpaceSizeUom = areaUomAbbrev(lease?.ref_total_leased_space_uom_desc);
            return `${formatNumber(leasedSpaceSize, 'en')} ${leasedSpaceSizeUom}`;
          },
        },
        { label: 'overlayDetails.startDate', displayFn: ({ lease }) => formatDate(lease.lease_start_date) },
        { label: 'overlayDetails.endDate', displayFn: ({ lease }) => formatDate(lease.lease_end_date) },
        { label: 'overlayDetails.occupancyDate', displayFn: ({ lease }) => formatDate(lease.occupancy_date) },
      ],
    },
  ],
  subsets: [
    {
      label: 'overlayDetails.termTime',
      inputType: 'progressBar',
      labelFn: space => {
        const { startDate, endDate } = space.getSpaceDates();
        const isDateMasked = isMasked(startDate) && isMasked(endDate);
        const isDateMissing = !startDate || !endDate;
        const mask = '*****';
        return isDateMissing ? null : isDateMasked ? mask : yearsAndMonthsTerm(startDate, endDate);
      },
      displayFn: space => {
        const { startDate, endDate } = space.getSpaceDates();
        const { termPercentage } = yearsAndMonthsTerm(startDate, endDate);
        return termPercentage ?? 0;
      },
    },
    {
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_Floor),
      dataField: 'floorNumber',
      inputType: 'textbox',
      rawValueFn: space => space.getFloorNumber(),
      displayFn: space => space.getFloorNumber(),
      id: LeaseColumns.FloorSuite_Floor,
    },
    {
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_Suite),
      dataField: 'suiteNumber',
      inputType: 'textbox',
      rawValueFn: space => space.suiteNumber,
      displayFn: space => space.suiteNumber,
      class: 'suite',
      id: LeaseColumns.FloorSuite_Suite,
    },
    {
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_FullFloor),
      dataField: 'full_floor_f',
      inputType: 'dropdown',
      lookupCategory: '/lookup/yn',
      rawValueFn: space => space.lease?.full_floor_f,
      displayFn: space => (space.lease?.full_floor_f === true ? 'Y' : space.lease?.full_floor_f === false ? 'N' : '-'),
      id: LeaseColumns.FloorSuite_FullFloor,
    },
    {
      dataField: 'spaceSize',
      formGroup: 'formGroupSpace',
      inputType: 'numeric',
      unit: {
        dataField: 'spaceSizeUOM',
        formGroup: 'formGroupSpace',
        inputType: 'dropdown',
        lookupCategory: AreaUrl,
        rawValueFn: space => space.getAreaSpaceUOM(),
        displayFn: space => space.getAreaSpaceUOM(),
      },
      rawValueFn: space => space.getAreaSpace(),
      displayFn: (space, column) => {
        const spaceSize = space.getAreaSpace();
        if (spaceSize === null) {
          return null;
        }
        return formatNumber(spaceSize, 'en-US', column?.options?.format ?? '.0-2');
      },
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_SpaceSize),
      isAreaField: true,
      id: LeaseColumns.FloorSuite_SpaceSize,
    },
    {
      dataField: 'leaseStartDate',
      inputType: 'date',
      validators: [leaseDateValidator('start')],
      rawValueFn: space => {
        const { startDate } = space.getSpaceDates();
        return isMasked(startDate) ? startDate : formatDate(startDate);
      },
      displayFn: space => {
        const { startDate } = space.getSpaceDates();
        return isMasked(startDate) ? startDate : formatDate(startDate);
      },
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_StartDate),
      id: LeaseColumns.FloorSuite_StartDate,
    },
    {
      dataField: 'leaseEndDate',
      inputType: 'date',
      validators: [leaseDateValidator('end')],
      rawValueFn: space => {
        const { endDate } = space.getSpaceDates();
        return isMasked(endDate) ? endDate : formatDate(endDate);
      },
      displayFn: space => {
        const { endDate } = space.getSpaceDates();
        return isMasked(endDate) ? endDate : formatDate(endDate);
      },
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_EndDate),
      id: LeaseColumns.FloorSuite_EndDate,
    },
    {
      dataField: 'spaceOccupancyDate',
      inputType: 'date',
      rawValueFn: space => {
        const { occupancyDate } = space.getSpaceDates();
        return isMasked(occupancyDate) ? occupancyDate : formatDate(occupancyDate);
      },
      displayFn: space => {
        const { occupancyDate } = space.getSpaceDates();
        return isMasked(occupancyDate) ? occupancyDate : formatDate(occupancyDate);
      },
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_OccupancyDate),
      id: LeaseColumns.FloorSuite_OccupancyDate,
    },
    {
      dataField: 'spaceLeaseStatus',
      inputType: 'dropdown',
      validators: [isValidSpaceTenancyStatus],
      lookupCategory: '/lookup/ref/country?lookupcategory=TENANT_STATUS',
      rawValueFn: space => space.getTenantStatus(),
      displayFn: space => space.getTenantStatus(),
      labelFn: columns => getColumnLabel(columns, LeaseColumns.FloorSuite_Status),
      id: LeaseColumns.FloorSuite_Status,
    },
    {
      rawValueFn: space => space.generateTenantRepAgency(),
      displayFn: space => space.generateTenantRepAgency(),
      labelFn: columns => getColumnLabel(columns, LeaseColumns.Companies_TenantAgency),
    },
    {
      rawValueFn: space => space.getPropertyType(),
      displayFn: space => space.getPropertyType(),
      labelFn: columns => getColumnLabel(columns, LeaseColumns.Property_SpaceUsageType),
      hideOnEdit: true,
    },
    {
      rawValueFn: space => (space.lease.id != null ? space.leaseId.toString() : null),
      displayFn: space => (space.lease.id != null ? space.leaseId.toString() : null),
      navigationToEntity: { entity: SuiteType.Lease },
      class: 'link',
      labelFn: columns => getColumnLabel(columns, LeaseColumns.Admin_CompID),
    },
    {
      labelFn: columns => getColumnLabel(columns, LeaseColumns.LeaseCompDetails_Notes),
      dataField: 'notes',
      inputType: 'textarea',
      class: 'notes',
      rawValueFn: space => space.getNotes(),
      displayFn: space => space.getNotes(),
      id: LeaseColumns.LeaseCompDetails_Notes,
    },
  ],
  footer: {
    label: 'overlayDetails.lastUpdate',
    class: 'last-update',
    displayFn: space => {
      const { lastUpdatedDate, lastUpdatedBy } = space.getRecordTimestamp();
      return generateLastUpdatedByMessage(lastUpdatedDate, lastUpdatedBy);
    },
  },
  disableMenu: (record: Record<string, any>) => record?.isEditableByUser,
};
